import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import Text from '@components/text';
import { generateScales } from '@helpers/generate-media-queries';
import { defaultTheme } from '@styles/theme.styled';
import { underlineTransition } from '@components/link/link.styled';
import { NewsletterWrapper } from '@components/newsletter-modal/newsletter-modal.styled';

const createItemLinkCSS = (theme: typeof defaultTheme) =>
  css`
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.white};
    font-family: ${theme.fontFamily.text};
    ${generateScales('font-size', '0.9375rem', '1rem', theme)};
    line-height: ${theme.lineHeights.sm};
    text-decoration: none;
    cursor: pointer;
    ${underlineTransition}

    ${underlineTransition}
  `;

export const StyledText = styled(Text)`
  ${({ theme }) => css`
    ${generateScales('font-size', '0.9375rem', '1rem', theme)};
  `}
`;

const createLinkCSS = (theme: typeof defaultTheme) =>
  css`
    margin-bottom: 0;
    text-align: center;
    ${underlineTransition}

    @media (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
    }
  `;

export const ItemLink = styled(Link)`
  ${({ theme }) => createItemLinkCSS(theme)}
`;

export const ExternalItemLink = styled.a`
  ${({ theme }) => createItemLinkCSS(theme)}
`;

export const StyledLink = styled(ItemLink)`
  ${({ theme }) => css`
    ${createLinkCSS(theme)}
    color: ${theme.colors.primary};
  `}
`;

export const ExternalLink = styled(ExternalItemLink)`
  ${({ theme }) => css`
    ${createLinkCSS(theme)}
    color: ${theme.colors.primary};
  `}
`;

export const StyledFooter = styled.footer`
  ${({ theme }) => css`
    position: relative;
    z-index: 98;
    background-color: ${theme.backgroundColors.dark};
    color: ${theme.colors.secondary};
    display: flex;
    flex-direction: column;
    row-gap: 7rem;
    padding: 7rem 0;

    @media (max-width: ${theme.breakpoints.xl}) {
      padding: 6rem 0;
      row-gap: 6rem;
    }

    @media (max-width: ${theme.breakpoints.lg}) {
      padding: 5rem 0;
      row-gap: 5rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      padding: 4rem 0;
      row-gap: 4rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 3rem;
    }
  `}
`;

export const TopContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    @media (max-width: ${theme.breakpoints.lg}) {
      flex-direction: column;
      gap: 5rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      gap: 4rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 3rem;
    }
  `}
`;

export const MainContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 4rem;
    width: 100%;
    max-width: 48rem;
    height: 100%;
    position: relative;
    z-index: 1;

    @media (max-width: ${theme.breakpoints.lg}) {
      gap: 5rem;
      max-width: unset;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      flex-direction: column;
      row-gap: 4rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 3rem;
    }
  `}
`;

export const LogoContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;

    @media (max-width: ${theme.breakpoints.lg}) {
      max-width: 12rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
    }
  `}
`;

export const Logo = styled.div<{ imageUrl: string }>`
  ${({ theme, imageUrl }) => css`
    width: 100%;
    height: 4.5rem;
    background-color: ${theme.colors.primary};
    mask: url(${imageUrl}) no-repeat left center / contain;
    -webkit-mask: url(${imageUrl}) no-repeat left center / contain;

    @media (max-width: ${theme.breakpoints.lg}) {
      height: 4rem;
      max-width: 12rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      height: 3rem;
      max-width: 10rem;
    }
  `}
`;

export const LogoDescription = styled.p`
  ${({ theme }) => css`
    color: ${theme.colors.primary};
    font-family: ${theme.fontFamily.text};
    ${generateScales(
      'font-size',
      theme.fontSize.small.mobile,
      theme.fontSize.small.desktop,
      theme
    )};
    line-height: ${theme.lineHeights.lg};

    @media (max-width: ${theme.breakpoints.lg}) {
      width: 14rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      width: 100%;
    }
  `}
`;

export const LinksGrid = styled.nav`
  ${({ theme }) => css`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 4rem;
    row-gap: 3rem;

    @media (max-width: ${theme.breakpoints.lg}) {
      gap: 5rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      grid-template-columns: repeat(2, 1fr);
      gap: 4rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 3rem;
    }

    @media (max-width: ${theme.breakpoints.xs}) {
      grid-template-columns: 1fr;
    }
  `}
`;

export const LinksGridColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const ItemTitle = styled(Text)`
  margin-bottom: 1.25rem;
`;

export const LinkList = styled.ul``;

export const LinkListItem = styled.li`
  :not(:last-of-type) {
    margin-bottom: 0.75rem;
  }
`;

export const BottomContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    position: relative;
    z-index: 1;
    font-size: ${theme.fontSize.small.desktop};

    @media (max-width: ${theme.breakpoints.lg}) {
      flex-direction: column;
      row-gap: 1.5rem;
    }
  `}
`;

export const StyledSpan = styled.span`
  ${({ theme }) => css`
    color: ${theme.colors.white};
    font-family: ${theme.fontFamily.text};
    line-height: ${theme.lineHeights.sm};
    text-align: center;
  `}
`;

export const BottomLinksContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    column-gap: 1.25rem;

    @media (max-width: ${theme.breakpoints.md}) {
      margin: 0;
      flex-direction: column;
      row-gap: 1.5rem;
    }
  `}
`;

export const BottomLinks = styled.div`
  display: flex;
  column-gap: 1.25rem;
`;

export const SocialContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media (max-width: ${theme.breakpoints.lg}) {
      flex-direction: row;
      gap: 2rem;
      align-items: center;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      gap: 1rem;
    }
  `}
`;

export const SocialIconWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    column-gap: 1rem;

    @media (max-width: ${theme.breakpoints.md}) {
      gap: 0.875rem;
    }
  `}
`;

export const MetaContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 3rem;
    width: 100%;
    max-width: 20rem;

    @media (max-width: ${theme.breakpoints.lg}) {
      max-width: unset;
      flex-direction: row;
      row-gap: 2rem;
      justify-content: space-between;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      row-gap: 4rem;
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
      row-gap: 3rem;
    }
  `}
`;

export const SocialMedia = styled.a`
  ${({ theme }) => css`
    height: 2.5rem;
    width: 2.5rem;
    border-radius: 1.25rem;
    background-color: ${theme.colors.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.125s ease-in-out;

    :hover {
      background-color: ${theme.colors.white};
      transform: scale(1.125);
    }

    :active {
      transform: scale(0.9375);
    }

    img {
      height: 1.25rem;
      width: 1.25rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      height: 2rem;
      width: 2rem;
      border-radius: 1rem;

      img {
        height: 1rem;
        width: 1rem;
      }
    }
  `}
`;

export const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  width: 100%;
`;

export const LocationContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
`;

export const NewsletterContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: 5rem;
    width: 100%;

    ${NewsletterWrapper} {
      width: 100%;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 4rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
      gap: 3rem;
    }
  `}
`;
